export type Stat = {
  value: string;
  label: string;
};
export type Meta = {
  label: string;
  value: string;
};
export const hero = {
  eyebrow: 'Case Study — Lumen Platform',
  title: 'Designing the Interactive Map for FGE NexantECA’s Lumen Platform',
  subtitle: 'Brought in to drive the final stretch of UI and UX for FGE Nexant’s new product, Lumen. Focusing on the platform’s interactive map, turning complex market and scenario data into a clear, visual way for users to explore global gas and LNG activity.'
};
export const meta: Meta[] = [{
  label: 'Client',
  value: 'FGE Nexant'
}, {
  label: 'Role',
  value: 'Individual Contributor, Product Designer'
}, {
  label: 'Timeline',
  value: 'Jan – Mar 2026'
}, {
  label: 'Services',
  value: 'Product Design, UX'
}];
export const heroStats: Stat[] = [{
  value: 'On time',
  label: 'MVP shipped on a fixed launch date despite joining mid-project'
}, {
  value: '14',
  label: 'Data variables unified across 3 layer categories'
}, {
  value: '2 → 1',
  label: 'Sign-off cycles cut, turning a week-long review into a few days'
}, {
  value: 'One',
  label: 'Brand and interface unifying FGE Nexant’s data points'
}];
export const constraints: string[] = ['A fixed deadline set before I joined', 'An existing design system I hadn’t built', 'Tight engineering bandwidth throughout', "Scope that kept shifting"];
export const processSteps = [{
  title: 'Contextual research',
  body: 'Sat with analysts and traders to understand how they actually consume market data day to day, rather than how we assumed they did.'
}, {
  title: 'Audit of both estates',
  body: "Audited the existing tooling for redundancy and friction, and used the overlap to prioritise."
}, {
  title: 'Prototype, then re-tool',
  body: "Prototyped layer interactions in Mapbox, then moved to MapTiler when licensing and performance became a problem, treating the tooling change as a design decision rather than a blocker."
}, {
  title: 'Clickable prototypes',
  body: "Built clickable Figma prototypes and in app designs so stakeholders could react to real interactions instead of imagining them from a description, which sped up sign-off considerably."
}];
export const rituals = [{
  title: 'Daily stand-up',
  body: 'The team shared their focus for the day, surfacing blockers before they cost time.'
}, {
  title: 'Regular team crits',
  body: 'Design decisions were pressure-tested in the open, with engineers in the room.'
}, {
  title: 'End-of-week round-ups',
  body: 'Kept analysts, engineers and both design workstreams aligned on one picture of progress.'
}];
export const craftPoints = [{
  title: 'Modular by necessity',
  body: 'Components were designed to be reconfigured without a full redesign, because scope kept shifting underneath us.'
}, {
  title: 'Progress you can see',
  body: 'Kept stakeholders aligned through visual progress artefacts rather than written status reports.'
}, {
  title: 'Build-ready documentation',
  body: 'Documented designs so engineering could build straight from them, which cut back-and-forth given how tight bandwidth was.'
}];
export const layerCategories = [{
  name: 'Infrastructure Type',
  count: '3',
  items: ['Pipelines', 'Liquefaction', 'Regas']
}, {
  name: 'Status',
  count: '9',
  items: ['In operation', 'Under construction', 'Mothballed', 'Decommissioned', 'Suspended', 'Shelved', 'Planned (Likely)', 'Planned (Possible)', 'Planned (Proposed)']
}, {
  name: 'Route Geometry',
  count: '2',
  items: ['GEM verified', 'FGE estimated arc']
}];
export const shipped = ['MVP launched on time despite joining late.', 'Live layer controls across 14 toggleable data points, letting users move between crude, gas, LNG and green energy infrastructure in one view instead of switching between tools.', 'A reusable, documented design system that can evolve after launch without a full redesign.', 'FGE and NexantECA’s separate offerings unified into one interface, cutting cognitive load for analysts working across the combined product.'];
export const differently = [{
  title: 'Join earlier',
  body: 'Being part of the project from an earlier stage would have let me understand where and why component adherence was breaking down before it became a late-stage hand-off problem, rather than inheriting the gap with no time left to fix its cause.'
}, {
  title: 'Push for tooling decisions sooner',
  body: 'The mapping software changed halfway through, moving from Mapbox to MapTiler for licensing and performance reasons. I’d explore the tooling options earlier next time, specifically to get costs properly reflected upfront rather than absorbing a mid-project switch.'
}];
export const signOff = ['On-Time Launch', '14 Data Variables Unified', 'Fixed External Deadline', 'Cross-Org Design System'];
export const images = {
  heroDevice: 'https://cdn.magicpatterns.com/uploads/75fUhFGUy6VzRFJYK4H9gm/169ce4bfed61c280e99d19d67_Frame_2095586548.webp',
  globeDark: 'https://cdn.magicpatterns.com/uploads/vUggozSWzFsSugP18psM38/Global_-_Infrastructure_1.4.png',
  pipelinesGlobe: 'https://cdn.magicpatterns.com/uploads/dxTvMZjsiWdWVU44Nay7MS/Global_-_Infrastructure_Pipelines_1.1.png',
  detailPanel: 'https://cdn.magicpatterns.com/uploads/9SFVDr1XaEVucMXiedxZQ1/Global_-_Infrastructure_Pipelines_1.2.png',
  routeMapping: 'https://cdn.magicpatterns.com/uploads/g44XEMMBZczXW4ofmeUBzR/Global_-_Infrastructure_Route_mapping_1.1.png',
  designStyle: 'https://cdn.magicpatterns.com/uploads/n1H5ZWewBKPBGMNcpi8opA/MapTiler_Design_Style.png',
  components: 'https://cdn.magicpatterns.com/uploads/j8CwuwAA9wzwrcj4XWbJDW/Screenshot_2026-07-23_at_10.53.32.png',
  boardOverview: 'https://cdn.magicpatterns.com/uploads/kgNP472PHw2jBwo77AnZf2/Screenshot_2026-07-19_at_11.15.55.png',
  mapTilerTool: 'https://cdn.magicpatterns.com/uploads/6ubcUWReerk8jJqomDCXwx/map_tiler_1.jpg'
};